import { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Button, TextField, DialogActions, Grid, Typography, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from '../../../../../reusable-components/snackbar';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import axiosInstance from '../../../../../utils/axios';
import { objectToFormData } from '../../../../../api/utils';
import SimpleAutocompleteProps from '../../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import BookingAccountSearch from '../../facility/BookingAccountSearch';
import { bookingTypeIsVendorAccountSeachable } from '../../facility/BookingAccountSearchHelper';

BookingAccountForm.propTypes = {
  setAccounts: PropTypes.func,
  editAccount: PropTypes.object,
  bookingVendors: PropTypes.array,
  vendors: PropTypes.array,
  close: PropTypes.func,
};

export default function BookingAccountForm({ setAccounts, editAccount, close, bookingVendors, vendors }) {
  const [saving, setSaving] = useState(false);

  const [value, setValue] = useState({
    bookingVendor: editAccount ? bookingVendors.find(bookingVendor => bookingVendor.value === editAccount.bookingVendorId) : null,
    vendor: editAccount ? { value: editAccount.vendorId, label: editAccount.vendor } : null,
    account: editAccount?.account ?? '',
    subaccount: editAccount?.subaccount ?? ''
  });

  const handleSave = () => {   
    setSaving(true)
    axiosInstance.post('Vendor/VendorBookingAccountForm', objectToFormData({
      vendorId: value.vendor?.value,
      account: value.account,
      subaccount: value.subaccount,
      bookingVendorId: value.bookingVendor?.value,
      id: editAccount?.id
    })).then(res => {
      if (res.data) {
        const updatedAccount = { ...res.data, vendor: value.vendor?.label, bookingVendor: value.bookingVendor?.label };
        setAccounts(accounts => editAccount ? accounts.map(account => account.id === updatedAccount.id ? updatedAccount : account) : [...accounts, updatedAccount]);
        enqueueSnackbar('Booking account added successfully', { variant: 'success' });
        close();
      } else enqueueSnackbar('Failed to add booking account', { variant: 'error' });
    })
      .catch(() => enqueueSnackbar('Failed to add booking account', { variant: 'error' }))
      .finally(() => setSaving(false));
  };

  return <Dialog
    open
    onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      close();
    }}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle sx={{ textAlign: 'center' }}>
      {editAccount ? 'Edit' : 'Add'} Booking Account <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Booking Vendor*</Typography>
          <Autocomplete
            {...SimpleAutocompleteProps}
            renderInput={(params) => <TextField {...params} type="search" inputProps={{ ...params.inputProps, autoComplete: 'off' }} placeholder="Search or select" autoComplete="off"/>}
            value={value.bookingVendor}
            options={bookingVendors}
            onChange={(_, newValue) => setValue(prev => ({ ...prev, bookingVendor: newValue }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Vendor*</Typography>
          <Autocomplete
            {...SimpleAutocompleteProps}
            renderInput={(params) => <TextField {...params} type="search" inputProps={{ ...params.inputProps, autoComplete: 'off' }} placeholder="Search or select" autoComplete="off"/>}
            value={value.vendor}
            options={vendors?.map(vendor => ({ value: vendor.id, label: vendor.name })) ?? []}
            onChange={(_, newValue) => setValue(prev => ({ ...prev, vendor: newValue }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Account Number*</Typography>
          <TextField fullWidth size="small" value={value.account} onChange={e => setValue({ ...value, account: e.target.value })} autoComplete="off"/>         
        </Grid>
        {value.bookingVendor?.usesSubaccounts && <Grid item xs={12}>
          <Typography variant="subtitle1">Subaccount Number</Typography>
          <TextField fullWidth size="small" value={value.subaccount} onChange={e => setValue({ ...value, subaccount: e.target.value })} autoComplete="off"/>
        </Grid>}
        {bookingTypeIsVendorAccountSeachable(value.bookingVendor?.bookingAccountForSearch) && <Grid item xs={12}>
          <BookingAccountSearch bookingType={value.bookingVendor?.bookingAccountForSearch}
            onSelect={(val) => setValue(value => ({ ...value, account: val.account, subaccount: val.subaccount }))} />
        </Grid>}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={close}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!value.vendor || !value.bookingVendor || !value.account}
        loading={saving}
        sx={{ width: 150 }}
        onClick={handleSave}
      >
        {editAccount ? 'Save' : 'Add Account'}
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}